import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import { Badge, Button, tx } from "@/components/ui";
import { addDays, fmtDate, loc, todayIso } from "@/lib/dates";

type Orphan = {
  appointment_id: string; starts_at: string; ends_at: string; status: string;
  customer: { id: string; name: string; phone: string | null; favorite_staff_id: string | null };
  service: { id: string; name: Record<string, string> }; staff: { id: string; display_name: string };
  reason: "inactive" | "time_off" | "not_working" | "outside_shift"; candidates: { id: string; display_name: string; color: string }[];
};

/** Appointments whose technician is on leave, deactivated or no longer working that slot: reassign with one click.
 * The customer's favourite technician is starred; picking someone else is allowed but flagged. */
export function OrphansPanel({ storeId, tz, days = 60 }: { storeId: string; tz: string; days?: number }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const from = todayIso();
  const to = addDays(from, days);
  const q = useQuery({ queryKey: ["orphans", storeId, from, to], queryFn: () => api<Orphan[]>(`/app/orphans?store_id=${storeId}&date_from=${from}&date_to=${to}`), retry: false });
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reassign = useMutation({
    mutationFn: ({ id, staffId }: { id: string; staffId: string }) => api<{ ok: boolean; favorite_mismatch: boolean }>(`/app/appointments/${id}/reassign`, { method: "POST", body: JSON.stringify({ staff_id: staffId }) }),
    onSuccess: (r) => { setError(null); setNote(r.favorite_mismatch ? t("orphans.favoriteMismatch") : t("orphans.reassigned")); qc.invalidateQueries({ queryKey: ["orphans"] }); qc.invalidateQueries({ queryKey: ["calendar"] }); },
    onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")),
  });
  const list = q.data ?? [];
  if (!list.length) return null;
  const time = (iso: string) => new Date(iso).toLocaleTimeString(loc(i18n.language), { hour: "2-digit", minute: "2-digit", timeZone: tz });
  const day = (iso: string) => new Date(iso).toLocaleDateString("sv-SE", { timeZone: tz });
  return (
    <div className="rounded-xl border border-danger-soft bg-danger-soft p-3 text-sm">
      <div className="mb-1 flex flex-wrap items-center gap-2 font-medium text-danger">{t("orphans.title")} <Badge tone="red">{list.length}</Badge>{note && <span className="text-xs font-normal text-ok">{note}</span>}{error && <span className="text-xs font-normal text-danger">{error}</span>}</div>
      <ul className="space-y-1">
        {list.map((o) => (
          <li key={o.appointment_id} className="flex flex-wrap items-center gap-2">
            <Link className="underline" to={`/app/calendar?date=${day(o.starts_at)}&appt=${o.appointment_id}`}>{fmtDate(o.starts_at, i18n.language, tz)} {time(o.starts_at)}</Link>
            <span>{o.customer.name} · {tx(o.service.name, i18n.language)} · {o.staff.display_name}</span>
            <Badge tone="amber">{t(`orphans.reason.${o.reason}`)}</Badge>
            <span className="ml-auto flex flex-wrap items-center gap-1 text-xs">
              {o.candidates.length === 0 && <span className="text-muted">{t("orphans.noCandidates")}</span>}
              {o.candidates.map((c) => (
                <Button key={c.id} variant="ghost" className="!px-2 !py-0.5" disabled={reassign.isPending} title={o.customer.favorite_staff_id && o.customer.favorite_staff_id !== c.id ? t("orphans.notFavorite") : undefined} onClick={() => reassign.mutate({ id: o.appointment_id, staffId: c.id })}>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: c.color }} />{c.display_name}{o.customer.favorite_staff_id === c.id ? " ★" : ""}
                </Button>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
